import React, { useState } from 'react';
import { ChevronDown, AlertTriangle } from 'lucide-react';
import { EmailSenderIdentity } from '../../../services/email/types';

interface SenderSelectionProps {
  senders: EmailSenderIdentity[];
  selectedSender?: EmailSenderIdentity;
  onSenderChange: (sender: EmailSenderIdentity) => void;
  disabled?: boolean;
  loading?: boolean;
  error?: string | null;
  photoValidationErrors?: Record<string, string>;
}

export const SenderSelection: React.FC<SenderSelectionProps> = ({
  senders,
  selectedSender,
  onSenderChange,
  disabled = false,
  loading = false,
  error = null,
  photoValidationErrors = {}
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleSenderSelect = (sender: EmailSenderIdentity) => {
    onSenderChange(sender);
    setIsOpen(false);
  };

  // Prüfe ob der aktuell ausgewählte Sender ein Foto-Problem hat
  const selectedSenderHasPhotoError = selectedSender && photoValidationErrors[selectedSender.id];

  if (loading) {
    return (
      <div style={{ marginBottom: '16px' }}>
        <label style={{ 
          display: 'block', 
          fontSize: '14px', 
          fontWeight: '500', 
          color: '#374151', 
          marginBottom: '6px' 
        }}>
          E-Mail verschicken als:
        </label>
        <div style={{
          padding: '8px 12px',
          border: '1px solid #D1D5DB',
          borderRadius: '6px',
          backgroundColor: '#F9FAFB',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <div style={{ 
            width: '16px', 
            height: '16px', 
            border: '2px solid #6B7280', 
            borderTop: '2px solid transparent', 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite' 
          }}></div>
          <span style={{ fontSize: '14px', color: '#6B7280' }}>Lade Absender...</span>
        </div>
      </div>
    );
  }

  if (error && senders.length === 0) {
    return (
      <div style={{ marginBottom: '16px' }}>
        <label style={{ 
          display: 'block', 
          fontSize: '14px', 
          fontWeight: '500', 
          color: '#374151', 
          marginBottom: '6px' 
        }}>
          E-Mail verschicken als:
        </label>
        <div style={{
          padding: '8px 12px',
          border: '1px solid #FCA5A5',
          borderRadius: '6px',
          backgroundColor: '#FEF2F2',
          color: '#DC2626',
          fontSize: '14px'
        }}>
          ⚠️ {error}
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '16px', position: 'relative' }}>
      <label style={{ 
        display: 'block', 
        fontSize: '14px', 
        fontWeight: '500', 
        color: '#374151', 
        marginBottom: '6px' 
      }}>
        E-Mail verschicken als:
      </label>
      
      {/* Info über Fallback bei Fehlern */}
      {error && senders.length > 0 && (
        <div style={{
          fontSize: '12px',
          color: '#DC2626',
          marginBottom: '4px'
        }}>
          ⚠️ Datenbankfehler - verwende Fallback-Sender
        </div>
      )}
      
      {/* Kritische Fehlermeldung wenn ausgewählter Sender kein Foto hat */}
      {selectedSenderHasPhotoError && (
        <div style={{
          padding: '12px',
          margin: '8px 0',
          backgroundColor: '#FEF2F2',
          border: '1px solid #FCA5A5',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <AlertTriangle size={16} color="#DC2626" />
          <div>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#DC2626' }}>
              E-Mail-Versand nicht möglich
            </div>
            <div style={{ fontSize: '12px', color: '#DC2626', marginTop: '2px' }}>
              {photoValidationErrors[selectedSender.id]}
            </div>
          </div>
        </div>
      )}
      
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: '100%',
          padding: '8px 12px',
          border: selectedSenderHasPhotoError ? '1px solid #FCA5A5' : '1px solid #D1D5DB',
          borderRadius: '6px',
          backgroundColor: disabled ? '#F9FAFB' : (selectedSenderHasPhotoError ? '#FEF2F2' : '#FFFFFF'),
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '14px',
          color: disabled ? '#9CA3AF' : (selectedSenderHasPhotoError ? '#DC2626' : '#374151')
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {selectedSender?.photoUrl && !selectedSenderHasPhotoError ? (
            <img 
              src={selectedSender.photoUrl} 
              alt={selectedSender.fullName}
              style={{ 
                width: '24px', 
                height: '24px', 
                borderRadius: '50%',
                objectFit: 'cover'
              }}
            />
          ) : (
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              backgroundColor: selectedSenderHasPhotoError ? '#FCA5A5' : '#E5E7EB',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: '500',
              color: selectedSenderHasPhotoError ? '#DC2626' : '#6B7280'
            }}>
              {selectedSenderHasPhotoError ? '⚠️' : (selectedSender?.firstName?.charAt(0) || '?')}
            </div>
          )}
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontWeight: '500' }}>
              {selectedSender?.fullName || 'Wählen Sie einen Absender'}
            </div>
            {selectedSender?.title && (
              <div style={{ fontSize: '12px', color: selectedSenderHasPhotoError ? '#DC2626' : '#6B7280' }}>
                {selectedSender.title}
              </div>
            )}
          </div>
        </div>
        <ChevronDown 
          size={16} 
          style={{ 
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }} 
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && !disabled && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: '#FFFFFF',
          border: '1px solid #D1D5DB',
          borderRadius: '6px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          marginTop: '4px',
          maxHeight: '200px',
          overflowY: 'auto'
        }}>
          {senders.map((sender, index) => {
            const hasPhotoError = photoValidationErrors[sender.id];
            const isDisabled = Boolean(hasPhotoError);
            
            return (
              <button
                key={sender.id}
                type="button"
                onClick={() => handleSenderSelect(sender)}
                disabled={isDisabled}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  textAlign: 'left',
                  borderBottom: index < senders.length - 1 ? '1px solid #E5E7EB' : 'none',
                  transition: 'background-color 0.15s ease',
                  opacity: isDisabled ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isDisabled) {
                    e.currentTarget.style.backgroundColor = '#F9FAFB';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isDisabled) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {sender.photoUrl && !hasPhotoError ? (
                  <img 
                    src={sender.photoUrl} 
                    alt={sender.fullName}
                    style={{ 
                      width: '24px', 
                      height: '24px', 
                      borderRadius: '50%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: hasPhotoError ? '#FCA5A5' : '#E5E7EB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: hasPhotoError ? '#DC2626' : '#6B7280'
                  }}>
                    {hasPhotoError ? '⚠️' : sender.firstName.charAt(0)}
                  </div>
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontWeight: '500', 
                    color: hasPhotoError ? '#DC2626' : '#374151' 
                  }}>
                    {sender.fullName}
                    {sender.isPrimary && (
                      <span style={{ 
                        marginLeft: '8px',
                        fontSize: '11px',
                        backgroundColor: hasPhotoError ? '#FCA5A5' : '#3B82F6',
                        color: '#FFFFFF',
                        padding: '1px 6px',
                        borderRadius: '3px'
                      }}>
                        PRIMARY
                      </span>
                    )}
                    {hasPhotoError && (
                      <span style={{ 
                        marginLeft: '8px',
                        fontSize: '11px',
                        backgroundColor: '#DC2626',
                        color: '#FFFFFF',
                        padding: '1px 6px',
                        borderRadius: '3px'
                      }}>
                        KEIN FOTO
                      </span>
                    )}
                  </div>
                  {sender.title && (
                    <div style={{ 
                      fontSize: '12px', 
                      color: hasPhotoError ? '#DC2626' : '#6B7280' 
                    }}>
                      {sender.title}
                    </div>
                  )}
                  {hasPhotoError && (
                    <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
                      {photoValidationErrors[sender.id]}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};